import { isEmpty, result, extend } from 'underscore';
import { assign } from 'xstate';
import Promise from 'bluebird';
import { stepStateMachine } from '@bingads-webui-campaign-react/step-wizard-machine';
// import { modelConstraints } from '@bingads-webui-campaign-react/xandr-video-campaign-shared-components';
import { modelConstraints, getConvertedCurrencyDealMinimumBid, getAccountExchangeRateFromLocalStorage } from '@bingads-webui-campaign-react/xandr-video-campaign-shared-components';
import { isXandrDisplayAdsCampaign, isXandrVideoAdsCampaign, isXandrServing, ScopeLevel } from '@bingads-webui-campaign-react/constants';
import exclusionRepository from '@bingads-webui-campaign-legacy/advanced-settings/util/exclusion-repository';

import { loadBundleCountries, readLocationCriterions, readDayTimeCriterions, readTargetingCriterions, readAudienceCriterions, readPlacementCriterions } from '../../model/ad-group-criterions';
import { STEP_PATHS, getInitialAdGroupViewModel, AdGroupNegativeReferenceId } from '../../common/constants';
import { StepAdGroupSettings } from './step-ad-group-settings';
import { DeviceKeys, Devices } from './target/device';
import { saveAdGroup, getErrorsPlaceholders } from '../../model/ad-group-bulk';
import { viewModelToModelForCampaign, getDealDetails } from '../../model/campaign';
import { viewModelToModelForAdGroup, loadAdGroup, modelToViewModelForAdGroup } from '../../model/ad-group';

const needsLoading = () => true;
const hasCampaignData = context =>
  Boolean(result(context, ['stepsData', 'campaign', 'Id']));

const getCampaignId = context => result(context, ['data', 'campaign', 'data', 'Id']) || result(context, ['stepsData', 'campaign', 'Id']) || result(context, ['stepsData', 'currentCampaign', 'Id']);
const getAdGoupId = context => result(context, ['data', 'adGroup', 'data', 'Id']) || result(context, ['stepsData', 'adGroupId']);
const getBiddingScheme = context => result(context, ['stepsData', 'currentCampaign', 'BiddingScheme', 'Type']) || result(context, ['stepsData', 'campaign', 'BiddingScheme', 'Type']);
const getCampaignSubType = context => result(context, ['stepsData', 'currentCampaign', 'CampaignSubType']) || result(context, ['stepsData', 'campaign', 'CampaignSubType']);

const getInitDeviceTypes = (context, permissions) => {
  const campaignSubType = getCampaignSubType(context);
  const biddingScheme = getBiddingScheme(context);
  const isDisplayAds = isXandrDisplayAdsCampaign(campaignSubType);
  const isVideoAds = isXandrVideoAdsCampaign(campaignSubType);

  if (isDisplayAds) {
    return [DeviceKeys.Computers, DeviceKeys.Tablets, DeviceKeys.Smartphones];
  }

  if (isVideoAds && permissions.dynamic.XandrDeviceTarget) {
    return biddingScheme === 'ManualCpm' ? [DeviceKeys.Computers, DeviceKeys.Tablets, DeviceKeys.Smartphones] : [DeviceKeys.ConnectedTVs];
  }

  return Object.keys(Devices);
};

/**
 * Loads step data asynchronously. The returned promise must resolve
 * into a single object, opaque to the step, which will be saved
 * in the context under the `data` property.
 *
 * The function is called when the wizard enters the step.
 * The step will be blocked until the promise resolves.
 * @param {Object} context - the current context
 * @returns {Promise<Object>} a promise that must resolve
 * into a single object, opaque to the step, which will be saved
 * in the context under the `data` property.
 */
const loadData = async (context) => {
  const {
    cid, aid, serverContext, i18n, permissions,
  } = context.stepsData;
  const deal = result(context, ['stepsData', 'campaign', 'deal']);
  const campaignId = getCampaignId(context);
  const adGroupId = getAdGoupId(context);
  const deviceTypes = getInitDeviceTypes(context, permissions);

  let dealMinimumBid;
  if (deal) {
    const exchangeRateMapping = getAccountExchangeRateFromLocalStorage(serverContext);
    dealMinimumBid = getConvertedCurrencyDealMinimumBid(deal.MinimumBid, deal.Currency, serverContext.CurrentAccount.CurrencyCode, exchangeRateMapping.val);
  }

  let loadPromises = {
    adGroup: Promise.resolve(getInitialAdGroupViewModel({ i18n, dealMinimumBid, deviceTypes })),
    bundleCountriesAndLocationCriterions: loadBundleCountries({
      cid,
      aid,
      serverContext,
    }),
    dealDetails: deal || getDealDetails(cid, aid, campaignId, serverContext),
  };

  if (adGroupId && adGroupId !== AdGroupNegativeReferenceId) {
    loadPromises = extend(loadPromises, {
      adGroup: loadAdGroup(cid, aid, campaignId, adGroupId),
      locationCriterions: readLocationCriterions({
        cid, aid, campaignId, adGroupId,
      }),
      dayTimeCriterions: readDayTimeCriterions({
        cid, aid, campaignId, adGroupId,
      }),
      targetCriterions: readTargetingCriterions({
        cid, aid, campaignId, adGroupId,
      }),
      audienceCriterions: readAudienceCriterions({
        cid, aid, campaignId, adGroupId,
      }),
      placementCriterions: readPlacementCriterions({
        cid, aid, campaignId, adGroupId,
      }),
      exclusions: exclusionRepository.load(campaignId, adGroupId, ScopeLevel.AdGroup),
    });
  }

  const {
    adGroup,
    locationCriterions,
    dealDetails,
    bundleCountriesAndLocationCriterions,
    dayTimeCriterions,
    targetCriterions,
    audienceCriterions,
    placementCriterions,
    exclusions: [campaignExclusions, adgroupExclusions] = [null, null],
  } = await Promise.props(loadPromises);

  let results = {
    ...bundleCountriesAndLocationCriterions,
    adGroup,
    ...getErrorsPlaceholders(),
  };

  const campaign = isEmpty(context.stepsData.campaign) ? context.stepsData.currentCampaign : context.stepsData.campaign;

  if (adGroupId) {
    const adGroupData = modelToViewModelForAdGroup({
      adGroupModel: adGroup,
      locationCriterionsModel: locationCriterions ? locationCriterions.value : [],
      bundleCountries: bundleCountriesAndLocationCriterions.bundleCountries,
      dayTimeCriterionsModel: dayTimeCriterions ? dayTimeCriterions.value : [],
      targetCriterionsModel: targetCriterions ? targetCriterions.value : [],
      audienceCriterionsModel: audienceCriterions ? audienceCriterions.value : [],
      i18n,
      isDisplayAds: isXandrDisplayAdsCampaign(getCampaignSubType(context)),
      isVideoAds: isXandrVideoAdsCampaign(getCampaignSubType(context)),
      isXandrServing: isXandrServing(getCampaignSubType(context), campaign.CampaignSettings),
      placementCriterionsModel: placementCriterions ? placementCriterions.value : [],
      campaignExclusions,
      adgroupExclusions,
    });
    results = extend(results, {
      adGroup: adGroupData,
    });
  }

  if (dealDetails) {
    results.adGroup.data = await modelConstraints(dealDetails, results.adGroup.data, serverContext);
  }

  results.adGroup.data.genreRestricted = !context.stepsData.permissions.dynamic.GenreTargets || results.adGroup.data.genreRestricted;

  return results;
};

// must return `Promise.resolve(data)` or `Promise.reject(error)`
// the payload will be available in `onDone` and `onError` in event's `data` prop.
const saveData = (context) => {
  const campaign = isEmpty(context.stepsData.campaign) ? context.stepsData.currentCampaign : context.stepsData.campaign;

  return saveAdGroup({
    adGroup: context.data.adGroup,
    campaign,
    bundleLocationCriterions: context.data.bundleLocationCriterions,
    bundleCountries: context.data.bundleCountries,
    isDisplayPlus: true,
  }, {
    scenario: context.stepsData.scenarioName,
    activityLogger: context.stepsData.scenario.createActivity('saveAdGroup'),
    i18n: context.stepsData.i18n,
    serverContext: context.stepsData.serverContext,
  });
};

const isValid = context => context.data.adGroup.isValid;
const isDirty = context => context.data.adGroup.isDirty;
const hasServerErrors = context => !isEmpty(context.data.serverErrors) || !isEmpty(context.data.otherErrors) || !isEmpty(context.data.systemErrors);
export const needsSaving = context => isDirty(context) || hasServerErrors(context) || !context.data.adGroup.data.Id || context.data.adGroup.data.Id === AdGroupNegativeReferenceId;

const patchStepsData = assign({
  stepsData: (context/* , event */) => {
    const campaignViewModel = isEmpty(context.stepsData.campaign) ? { data: context.stepsData.currentCampaign } : { data: context.stepsData.campaign };

    return {
      ...context.stepsData,
      adGroup: {
        ...viewModelToModelForAdGroup({ adGroupViewModel: context.data.adGroup, i18n: context.stepsData.i18n }),
        deal: context.data.adGroup.data.deal,
      },
      ...hasCampaignData(context) ? {} :
        { campaign: viewModelToModelForCampaign(campaignViewModel) },
    };
  },
});

const clearErrors = assign({
  data: context => ({
    ...context.data,
    ...getErrorsPlaceholders(),
  }),
});

export const stepAdGroupWizardConfig = {
  id: STEP_PATHS.AD_GROUP,
  title: _TL_('Ad group'),
  StepMachine: stepStateMachine,
  StepMachineOptions: (stepInfo, initialStepsData) => ({
    machineId: STEP_PATHS.AD_GROUP,
    initialContext: {
      id: stepInfo.id,
      stepInfo,
      stepsData: initialStepsData,
      data: {
        adGroup: getInitialAdGroupViewModel({ i18n: initialStepsData.i18n, isDisplayAds: initialStepsData.isDisplayAds }),
        ...getErrorsPlaceholders(),
      },
      invalidSaveAttempt: 0,
    },
    actions: {
      patchStepsData,
      clearErrors,
    },
    guards: {
      needsLoading,
      needsSaving,
      isValid,
      isDirty,
    },
  }),
  StepComponent: StepAdGroupSettings,
  canNavigateBackWithoutSaving: true,
  stepRepositoryConfig: {
    saveData,
    loadData,
  },
};
